import { createEntityAdapter, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useAppDispatch } from '@shared/hooks';
import type { PromoVideo } from '@shared/types/promo-videos';
import { useAppSelector } from '@src/library/hooks/common';

const promoAdapter = createEntityAdapter({
  selectId: (promo: PromoVideo) => promo.id,
});

const promoSlice = createSlice({
  name: 'promo',
  initialState: promoAdapter.getInitialState<{ selectedPromo: PromoVideo | null }>({
    selectedPromo: null,
  }),
  reducers: {
    promoAdded: promoAdapter.addOne,
    promosAdd: promoAdapter.addMany,
    promosRemove: promoAdapter.removeAll,
    promoRemove: promoAdapter.removeOne,
    promosSet: promoAdapter.setAll,
    promoSet: promoAdapter.setOne,
    promosReceived(state, action: PayloadAction<{ promos: PromoVideo[] }>) {
      promoAdapter.setAll(state, action.payload.promos);
    },
    promoSelected(state, action: PayloadAction<PromoVideo | null>) {
      state.selectedPromo = action.payload;
    },
    promoReset(state) {
      state.selectedPromo = null;
    },
  },
});

export const promoReducer = promoSlice.reducer;
export const promoActions = promoSlice.actions;

export const promosSelectors = promoAdapter.getSelectors();

export const usePromo = (id: number) => {
  const dispatch = useAppDispatch();
  const promo = useAppSelector((state) => promosSelectors.selectById(state.promoReducer, id));

  const addPromo = (promo: PromoVideo) => dispatch(promoActions.promoAdded(promo));

  const removePromo = (id: number) => dispatch(promoActions.promoRemove(id));

  const setPromo = (promo: PromoVideo) => dispatch(promoActions.promoSet(promo));

  const resetPromo = () => dispatch(promoActions.promoReset());

  const selectPromo = (promo: PromoVideo | null) => dispatch(promoActions.promoSelected(promo));

  return {
    promo,
    addPromo,
    setPromo,
    removePromo,
    resetPromo,
    selectPromo,
  };
};

export const usePromoVideos = () => {
  const dispatch = useAppDispatch();
  const addPromoVideos = (promos: PromoVideo[]) => dispatch(promoActions.promosAdd(promos));

  const addPromo = (promo: PromoVideo) => dispatch(promoActions.promoAdded(promo));
  const setPromo = (promo: PromoVideo) => dispatch(promoActions.promoSet(promo));

  return {
    addPromoVideos,
    addPromo,
    setPromo,
  };
};
